'use client';

import { Info, FileText, Receipt, CheckCircle } from 'lucide-react';
import { type JSX } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface InformationPageProps {
  onInputChange: (section: string, field: string, value: any) => void;
  formData: Record<string, any>;
}

function InformationPage(_props: Readonly<InformationPageProps>): JSX.Element {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div className="mb-8 text-center">
        <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
          {t.informationStepTitle}
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
          {t.wizardOverview}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Registration Process Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              {t.registrationProcess}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-gray-600 dark:text-gray-300">{t.processSteps}</p>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepContactPerson}
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepCompanyInfo}
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepFinancialYear}
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepRepresentatives}
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepCompanySignature}
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                {t.stepOtherInfo}
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Cost Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-green-600" />
              {t.costInformation}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {t.pdfCost}
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t.costDescription}</p>
          </CardContent>
        </Card>
      </div>

      {/* What You Will Receive */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            {t.whatYouReceive}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-gray-600 dark:text-gray-300">{t.receiveItems}</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <FileText className="mt-0.5 h-6 w-6 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-100">{t.officialPdf}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              <Receipt className="mt-0.5 h-6 w-6 text-green-600 dark:text-green-400" />
              <div>
                <p className="font-medium text-green-900 dark:text-green-100">
                  {t.invoiceDocument}
                </p>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <p className="text-center text-sm text-gray-600 dark:text-gray-300">{t.deliveryMethod}</p>
        </CardContent>
      </Card>

      {/* Ready to Start */}
      <div className="py-6 text-center">
        <div className="mx-auto max-w-md rounded-lg bg-linear-to-r from-blue-50 to-purple-50 p-4 dark:from-blue-900/20 dark:to-purple-900/20">
          <p className="font-medium text-gray-800 dark:text-gray-200">{t.getStarted}</p>
        </div>
      </div>
    </div>
  );
}

export default InformationPage;
